import React, { useCallback, useEffect, useState } from 'react';
import { t } from 'i18next';
import { useSetRecoilState } from 'recoil';
import { SelectValue } from 'antd/lib/select';

import {
  ELMA365AppModelInput,
  ELMA365SourceParametersSettingsOutput,
  IngestionPipelineOutput,
  IngestionSourceParametersOutput,
} from '../../../../../../../gamma-api';
import { alertsSelectorAdd } from '../../../../../../recoil';
import { MAIN_CLASS_NAME } from '../const';
import { SourceModalParametersStep } from '../../SourceModal';
import { FormAPI } from '../../../../../../types';

import ParametersExtra from './ParametersExtra';
import ParametersMain from './ParametersMain';
import { useElma365Apps } from './useElma365Apps';

export interface ISourceSettingsELMA365Props {
  formRef: React.RefObject<FormAPI>;
  step: SourceModalParametersStep;
  pipeline?: IngestionPipelineOutput;
  parameters: IngestionSourceParametersOutput;
  onChange?: (parameters: IngestionSourceParametersOutput) => void;
}

const SourceSettingsELMA365: React.FC<ISourceSettingsELMA365Props> = ({
  formRef,
  step,
  pipeline,
  parameters,
  onChange,
}) => {
  const settings = parameters.settings as ELMA365SourceParametersSettingsOutput;

  const addAlert = useSetRecoilState(alertsSelectorAdd);

  const onError = useCallback((err: unknown) => {
    addAlert({
      content: t('Source settings: ELMA365: Failed to load applications'),
      type: 'error',
    });
    // eslint-disable-next-line no-console
    console.error(err);
  }, []);

  const { nss, apps, searchApps } = useElma365Apps(settings.connector_id, false, onError);

  const [selectedApp, setSelectedApp] = useState<ELMA365AppModelInput>();
  const [loading, setLoading] = useState(true);

  const fetchApps = async () => {
    if (!settings.connector_id) {
      return;
    }

    setLoading(true);
    await searchApps();
    setLoading(false);
  };

  useEffect(() => {
    if (settings.app_namespace && settings.app_code && Array.isArray(apps) && apps.length > 0) {
      const match = apps.find((a) => a.namespace === settings.app_namespace && a.code === settings.app_code);
      setSelectedApp(match);
    }
  }, [apps, settings.app_namespace, settings.app_code]);

  useEffect(() => {
    void fetchApps();
  }, [settings.connector_id]);

  const renderApps = useCallback(
    async (search?: string): Promise<SelectValue[]> => {
      const items = await searchApps(search);
      items.sort((x, y) => {
        const result = x.namespaceName.localeCompare(y.namespaceName);
        return result ? result : x.name.localeCompare(y.name);
      });
      return items.map((item) => ({
        value: item.value,
        key: item.key,
        label: (
          <>
            <span>
              {item.namespaceName} / {item.name}
            </span>
            <span>
              ({item.namespaceCode} / {item.code})
            </span>
          </>
        ),
      }));
    },
    [settings.connector_id]
  );

  const renderParameters = () => {
    switch (step) {
      case SourceModalParametersStep.Main:
        return (
          <ParametersMain
            ref={formRef}
            nsMap={nss}
            parameters={parameters}
            searchApps={renderApps}
            selectedApp={selectedApp}
            onChange={onChange}
          />
        );

      case SourceModalParametersStep.Extra:
        return (
          <ParametersExtra
            loading={loading}
            parameters={parameters}
            pipeline={pipeline}
            selectedApp={selectedApp}
            onChange={onChange}
          />
        );

      default:
        return null;
    }
  };

  return <div className={MAIN_CLASS_NAME}>{renderParameters()}</div>;
};

export default SourceSettingsELMA365;
